/* eslint-disable no-nested-ternary */
import { useMemo, useState, useEffect, useCallback } from 'react';
import type { MRT_Updater, MRT_SortingState, MRT_PaginationState } from 'material-react-table';

import { fPriceString } from 'src/utils/format-string';

import {
  useGetCart,
  useGetAllProductsMRT,
  useGetFavouritesCollections,
} from 'src/api/hooks/api-hooks';
import {
  WeightTypeEnum,
  MeasurementUnit,
  weightTypeLabels,
  PriceDisplayUnitEnum,
  getMeasurementunitLabel,
  getPriceDisplayUnitLabel,
} from 'src/api/models/enums';

export type SearchAndFilterData = {
  productName: string;
  availableFrom: Date | null;
  // isSmeProducer: boolean | undefined;
  // isDirectFromProducer: boolean | undefined;
  producerDistance: string;
  distributorSiteId: string | null;
  allergens: string[];
  categories: string[];
  certifications: string[];
  dietaryLifestyles: string[];
  countryOfOrigin: string[];
  productType: string | null;
  airfreighted: boolean | undefined;
  favouriteCollectionIds: string[];
};

interface UseCalculatedProductListParams {
  initialPaginationModel?: MRT_PaginationState;
  initialSortModel?: MRT_SortingState;
  filterSearchModel: SearchAndFilterData;
}

export function useCalculatedProductList({
  initialPaginationModel,
  initialSortModel,
  filterSearchModel,
}: UseCalculatedProductListParams) {
  const [paginationModel, setPagintionModel] = useState<MRT_PaginationState>(
    initialPaginationModel ?? { pageSize: 25, pageIndex: 0 }
  );

  const [sortModel, setSortModel] = useState<MRT_SortingState>(
    initialSortModel ?? [{ id: 'productName', desc: false }]
  );

  const {
    data: productsResponse,
    isLoading: loadingProducts,
    error,
  } = useGetAllProductsMRT(paginationModel, sortModel[0], filterSearchModel);

  const {
    data: favouritesCollections,
    isLoading: loadingFavouritesCollections,
    error: fetchFavouritesCollectionsError,
  } = useGetFavouritesCollections(1, 100);

  const { data: cart } = useGetCart();

  const calculateProducts = useMemo(() => {
    const productData = productsResponse?.data;
    if (!productData || productData.length === 0 || !cart) {
      return [];
    }

    const allCartItems = cart.baskets.flatMap((basket) => basket.items);

    return productData.map((product) => {
      const cartItem = allCartItems.find(
        (item) => item.productStockId === product.productStockId
      );
      const itemInCart = !!cartItem;
      const quantity = itemInCart ? parseFloat(cartItem.quantity) : 1;

      // Calculate display fields
      const measurementUnit = product.productTemplate.measurementUnit as MeasurementUnit;
      const priceDisplayUnit = product.productTemplate.priceDisplayUnit as PriceDisplayUnitEnum;
      const weightType = product.productTemplate.weightType as WeightTypeEnum;
      const activePricePerUnitLabel = `${product.activePrice.toFixed(2)}${getPriceDisplayUnitLabel(priceDisplayUnit)}`;
      const pricePerUnitLabel = `${fPriceString(product.price)}${getPriceDisplayUnitLabel(priceDisplayUnit)}`;

      const totalPriceValue = product.activePrice * quantity;

      return {
        ...product,
        productStockId: product.productStockId,
        name: product.productTemplate.name,
        producer: product.productTemplate.producer?.name,
        producerId: product.productTemplate.producer?.id,
        brand: product.productTemplate.origin?.brand || '---',
        activePrice: activePricePerUnitLabel,
        price: pricePerUnitLabel,
        pricePerUnit: product.price,
        pricePerStandardUnit: product.pricePerStandardUnit,
        measurementUnit: getMeasurementunitLabel(measurementUnit),
        weightType: weightTypeLabels[weightType],
        quantity,
        isInCart: itemInCart,
        cartQuantity: quantity,
        cartItemId: cartItem?.id || '',
        totalPrice: `£${totalPriceValue.toFixed(2)}`,
        earliestDeliveryDate: product.earliestDeliveryDate,
        hasLinkedFavouritesLists: product.linkedFavouriteListsIds.length > 0,
        surplusEntries: product.surplusEntries,
        isLoading: false,
      };
    });
  }, [productsResponse, cart]);

  const [localProducts, setLocalProducts] = useState(calculateProducts);

  useEffect(() => {
    setLocalProducts((prevLocalProducts) =>
      calculateProducts.map((calcProduct) => {
        const localProduct = prevLocalProducts.find(
          (p) => p.productStockId === calcProduct.productStockId
        );

        // Preserve pending local quantity edits (not yet committed to the cart)
        // so updating one row doesn't clobber uncommitted edits on other rows.
        if (localProduct && localProduct.quantity !== calcProduct.cartQuantity) {
          return {
            ...calcProduct,
            quantity: localProduct.quantity,
            totalPrice: localProduct.totalPrice,
          };
        }

        return calcProduct;
      })
    );
  }, [calculateProducts]);

  const handleQuantityChange = useCallback(
    (productStockId: string, delta: number) => {
      setLocalProducts((prevProducts) => {
        let hasChanged = false;
        const allCartItems = cart?.baskets.flatMap((basket) => basket.items) ?? [];

        const newProducts = prevProducts.map((product) => {
          if (product.productStockId === productStockId) {
            hasChanged = true;
            const newQuantity = Math.max(1, product.quantity + delta);

            // Find the original product to get the numeric activePrice
            const originalProduct = productsResponse?.data.find(
              (p) => p.productStockId === productStockId
            );

            const cartItem = allCartItems.find(
              (item) => item.productStockId === product.productStockId
            );
            const itemInCart = !!cartItem;
            const totalPriceValue = originalProduct ? originalProduct.activePrice * newQuantity : 0;

            return {
              ...product,
              quantity: newQuantity,
              totalPrice: `£${totalPriceValue.toFixed(2)}`,
              isInCart: itemInCart,
              cartItemId: cartItem?.id || '',
            };
          }
          return product;
        });

        return hasChanged ? newProducts : prevProducts;
      });
    },
    [productsResponse?.data, cart?.baskets]
  );

  const handlePaginationChange = useCallback((updater: MRT_Updater<MRT_PaginationState>) => {
    setPagintionModel(updater);
  }, []);

  const handleSortingChange = useCallback((updater: MRT_Updater<MRT_SortingState>) => {
    setSortModel(updater);
  }, []);

  const returnValue = useMemo(
    () => ({
      products: localProducts,
      productsTotalCount: productsResponse?.totalRecords,
      productsTotalPages: productsResponse?.totalPages,
      cartId: cart?.id,

      fetchProductsLoading: loadingProducts,
      fetchProductsError: error,

      paginationModel,
      sortModel,

      setSortModel: handleSortingChange,
      setPaginationModel: handlePaginationChange,

      personalFavouritesCollections: favouritesCollections?.personal,
      favouritesCollectionsData: favouritesCollections,
      fetchloadingFavouritesCollections: loadingFavouritesCollections,
      fetchFavouritesCollectionsError,

      handleQuantityChange,
    }),
    [
      localProducts,
      productsResponse?.totalRecords,
      productsResponse?.totalPages,
      cart?.id,
      loadingProducts,
      error,
      paginationModel,
      sortModel,
      favouritesCollections,
      loadingFavouritesCollections,
      fetchFavouritesCollectionsError,
      handleQuantityChange,
      handlePaginationChange,
      handleSortingChange,
    ]
  );

  return returnValue;
}
